/*
 * TICK-151 — the Admin warning for "public sales are live, and there is nowhere for buyers to pay".
 *
 * Owner decision 2026-10-12: JamTick never receives money on an organizer's behalf, so an Event with
 * no enabled account of its own gets no payment QR at all. Opening sales is not blocked by this —
 * the owner is told, on every Admin screen, until the Event has an enabled receiving account.
 *
 * The status comes from the server (`GET /admin/payment-receiver-status`), which owns the definition
 * of "public sales live". It is re-read whenever the payment-account screens change an account, so
 * the warning clears the moment an account is added or re-enabled, without a reload.
 */
import { useCallback, useEffect, useState } from 'react';
import { adminApi, type PaymentReceiverStatusDto } from '../../api';
import { Alert } from '../ui/Alert';
import { Button } from '../ui/Button';

export const PAYMENT_ACCOUNTS_CHANGED = 'jamtick:payment-accounts-changed';

/** Called by the payment-account screens after any list (re)load, so the warning re-reads its status. */
export function notifyPaymentAccountsChanged(): void {
  window.dispatchEvent(new Event(PAYMENT_ACCOUNTS_CHANGED));
}

/**
 * `enabled` is false for every role but the owner: payment accounts are owner-only on the server,
 * and a staff token would only collect a 403. `refreshKey` re-reads on navigation.
 */
export function usePaymentReceiverStatus(
  token: string,
  eventSlug: string | undefined,
  enabled: boolean,
  refreshKey?: unknown,
): PaymentReceiverStatusDto | null {
  const [status, setStatus] = useState<PaymentReceiverStatusDto | null>(null);

  const load = useCallback(async () => {
    if (!enabled || !token) { setStatus(null); return; }
    try {
      setStatus(await adminApi.getPaymentReceiverStatus(token, eventSlug));
    } catch {
      // Keep the last known answer: a failed poll must not hide a warning that was true a moment ago.
    }
  }, [token, eventSlug, enabled]);

  useEffect(() => { void load(); }, [load, refreshKey]);

  useEffect(() => {
    const onChange = () => { void load(); };
    window.addEventListener(PAYMENT_ACCOUNTS_CHANGED, onChange);
    return () => window.removeEventListener(PAYMENT_ACCOUNTS_CHANGED, onChange);
  }, [load]);

  return status;
}

export const MISSING_RECEIVER_TITLE = 'เปิดขายอยู่ แต่งานนี้ยังไม่มีบัญชีรับเงินที่เปิดใช้งาน';

export function missingReceiverMessage(status: PaymentReceiverStatusDto): string {
  const why = status.totalAccounts === 0
    ? 'ยังไม่ได้เพิ่มบัญชีรับเงิน'
    : `มี ${status.totalAccounts} บัญชี แต่ปิดใช้งานทั้งหมด`;
  return `${why} — ผู้ซื้อจองบัตรได้ แต่หน้าชำระเงินจะไม่มี QR หรือเลขบัญชีให้โอน `
    + 'JamTick ไม่รับเงินแทนผู้จัดงาน เพิ่มหรือเปิดใช้บัญชีรับเงินของงานนี้เพื่อให้ผู้ซื้อชำระเงินได้';
}

export function PaymentReceiverWarning({ status, onOpenPayments, variant = 'console' }: {
  status: PaymentReceiverStatusDto | null;
  /** Omitted while the owner is already on the payment-accounts screen. */
  onOpenPayments?: () => void;
  variant?: 'console' | 'classic';
}) {
  if (!status?.missingReceiver) return null;
  const message = missingReceiverMessage(status);

  if (variant === 'classic') {
    return (
      <div className="form-warn" role="alert" data-testid="payment-receiver-warning" style={{ marginBottom: 18 }}>
        <b>{MISSING_RECEIVER_TITLE}</b>
        <div style={{ marginTop: 4 }}>{message}</div>
        {onOpenPayments && (
          <button className="btn-link" style={{ marginTop: 8 }} onClick={onOpenPayments}>
            ไปที่บัญชีรับเงิน →
          </button>
        )}
      </div>
    );
  }

  return (
    <div data-testid="payment-receiver-warning" style={{ marginBottom: 16 }}>
      <Alert
        tone="danger"
        title={MISSING_RECEIVER_TITLE}
        action={onOpenPayments ? <Button variant="secondary" onClick={onOpenPayments}>ไปที่บัญชีรับเงิน</Button> : undefined}
      >
        {message}
      </Alert>
    </div>
  );
}
